import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { Poll as PollIcon } from '@mui/icons-material';
import { auth } from '../services/api';
import { useAuth } from '../services/AuthContext';

const Register: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setError('');
    if (username.trim().length < 3) {
      setError('Der Benutzername muss mindestens 3 Zeichen lang sein');
      return;
    }
    if (password.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein');
      return;
    }

    setLoading(true);
    try {
      const data = await auth.register(username.trim(), password);
      login(data);
      navigate('/');
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          (err.response ? 'Registrierung fehlgeschlagen' : 'Keine Verbindung zum Server. Bitte Internetverbindung prüfen.')
      );
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: { xs: 4, sm: 8 }
        }}
      >
        <Paper 
          elevation={2}
          sx={{ 
            p: { xs: 3, sm: 4 },
            borderRadius: 2,
            bgcolor: 'background.paper'
          }}
        >
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <PollIcon 
              color="primary" 
              sx={{ 
                fontSize: { xs: 40, sm: 48 },
                mb: 2
              }}
            />
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem' },
                fontWeight: 600
              }}
            >
              Konto erstellen
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Registrieren Sie sich, um Ihre Prognosen abzugeben
            </Typography>
          </Box>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 1
              }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Benutzername"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              required
              autoFocus
              autoComplete="username"
              inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5
                }
              }}
            />
            <TextField
              fullWidth
              label="Passwort"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
              helperText="Mindestens 6 Zeichen – bitte gut merken oder im Passwort-Manager speichern"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5
                }
              }}
            />
            <TextField
              fullWidth
              label="Passwort bestätigen"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5
                }
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={loading}
              sx={{ 
                mt: 3, 
                mb: 2,
                height: 48,
                fontSize: '1rem'
              }}
            >
              {loading ? 'Registrieren…' : 'Registrieren'}
            </Button>
          </form>

          <Box sx={{ 
            mt: 2, 
            textAlign: 'center',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}>
            <Typography variant="body2" color="text.secondary">
              Bereits registriert?
            </Typography>
            <Button
              color="primary"
              onClick={() => navigate('/login')}
              sx={{ 
                fontWeight: 500,
                textDecoration: 'underline'
              }}
            >
              Zum Login
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register; 